/**
 * QuickAddButton — reusable '+' popover button for Master items.
 * Used next to ingredient pickers, category pickers, etc.
 *
 * Usage:
 *   <QuickAddIngredient onAdded={(newIng) => ...refresh + auto-select...} />
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";

const UNITS = ["g", "kg", "ml", "ltr", "pcs", "dozen"];

export function QuickAddIngredient({ onAdded, size = "sm", label = "" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", unit: "g", cost_per_unit: 0, current_stock: 0 });
  const reset = () => setF({ name: "", unit: "g", cost_per_unit: 0, current_stock: 0 });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Ingredient name required");
    setSaving(true);
    try {
      const r = await api.post("/ingredients", { ...f, cost_per_unit: Number(f.cost_per_unit) || 0, current_stock: Number(f.current_stock) || 0 });
      toast.success(`"${r.data.name}" added to master · used in this recipe`);
      setOpen(false); reset();
      onAdded?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button type="button" size={size} variant="outline" data-testid="quick-add-ingredient-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FEEBC8]" title="Quick add new ingredient to master">
          <Plus className="w-3.5 h-3.5" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader><DialogTitle className="font-[Outfit]">Quick Add Ingredient</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800">
            The new ingredient will be saved to the master and instantly available in this recipe.
          </div>
          <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Fresh Cream 25% Fat" data-testid="quick-ing-name" autoFocus /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Unit</Label>
              <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
                <SelectTrigger data-testid="quick-ing-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cost / unit (₹)</Label><Input type="number" step="0.01" value={f.cost_per_unit} onChange={(e) => setF({ ...f, cost_per_unit: e.target.value })} data-testid="quick-ing-cost" /></div>
            <div><Label>Current stock</Label><Input type="number" value={f.current_stock} onChange={(e) => setF({ ...f, current_stock: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !f.name.trim()} data-testid="quick-ing-save" className="bg-[#C05621] text-white">
            {saving ? "Saving…" : "Save & Use"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickAddProduct({ onAdded, size = "sm", label = "" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { name: "", sku: "", category: "Trading Goods", unit: "pcs", sale_price: 0, mrp: 0, gst_rate: 5, mfg_date: "", expiry_date: "" };
  const [f, setF] = useState(empty);
  const reset = () => setF(empty);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Product name required");
    if (!f.mfg_date || !f.expiry_date) return toast.error("Mfg Date & Expiry Date are required");
    setSaving(true);
    try {
      const sku = (f.sku || "").trim() || `TRD-${Date.now().toString().slice(-6)}`;
      const r = await api.post("/products", {
        ...f, sku,
        sale_price: Number(f.sale_price) || 0,
        mrp: Number(f.mrp) || Number(f.sale_price) || 0,
        gst_rate: Number(f.gst_rate) || 0,
      });
      toast.success(`"${r.data.name}" added to Products`);
      setOpen(false); reset();
      onAdded?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button type="button" size={size} variant="outline" data-testid="quick-add-product-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FEEBC8]" title="Quick add a new trading product">
          <Plus className="w-3.5 h-3.5" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader><DialogTitle className="font-[Outfit]">Quick Add Product</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800">
            Saved to the master Products list and instantly available for this purchase.
          </div>
          <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Coca-Cola 500ml" data-testid="quick-prod-name" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU (auto if blank)</Label><Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} data-testid="quick-prod-sku" /></div>
            <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="quick-prod-category" /></div>
            <div><Label>Unit</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="quick-prod-unit" /></div>
            <div><Label>GST %</Label><Input type="number" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} data-testid="quick-prod-gst" /></div>
            <div><Label>MRP (₹)</Label><Input type="number" value={f.mrp} onChange={(e) => setF({ ...f, mrp: e.target.value })} data-testid="quick-prod-mrp" /></div>
            <div><Label>Sale Price (₹)</Label><Input type="number" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} data-testid="quick-prod-price" /></div>
            <div><Label>Mfg Date *</Label><Input type="date" value={f.mfg_date} onChange={(e) => setF({ ...f, mfg_date: e.target.value })} data-testid="quick-prod-mfg-date" /></div>
            <div><Label>Expiry Date *</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="quick-prod-expiry-date" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !f.name.trim()} data-testid="quick-prod-save" className="bg-[#C05621] text-white">
            {saving ? "Saving…" : "Save & Use"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickAddCategory({ onAdded, size = "sm", label = "" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const save = async () => {
    if (!name.trim()) return toast.error("Category name required");
    const clean = name.trim().toLowerCase().replace(/\s+/g, "_");
    setSaving(true);
    try {
      // Categories are free-text on Products in this app — persist as a settings entry for reuse.
      const existing = (await api.get("/settings").catch(() => ({ data: {} }))).data || {};
      const cats = new Set([...(existing.product_categories || []), clean]);
      await api.patch("/settings", { product_categories: Array.from(cats) }).catch(() => {});
      toast.success(`"${clean}" added — you can now pick it`);
      setOpen(false); setName("");
      onAdded?.(clean);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size={size} variant="outline" data-testid="quick-add-category-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FEEBC8]" title="Quick add new category">
          <Plus className="w-3.5 h-3.5" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm bg-white">
        <DialogHeader><DialogTitle className="font-[Outfit]">Quick Add Category</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Category name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. sponge_cake" data-testid="quick-cat-name" autoFocus />
          <div className="text-[10px] text-[#8B7355]">Spaces become underscores; lowercased. Persisted in Settings for reuse.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !name.trim()} data-testid="quick-cat-save" className="bg-[#C05621] text-white">{saving ? "Saving…" : "Save & Use"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
